/**
 * Label print / export helpers: browser print, PNG download, PDF download.
 */
import { jsPDF } from 'jspdf';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function labelCaption(label) {
  return (
    label.unit_label ||
    label.unit_display ||
    label.unit_code ||
    [label.product_name, label.sku, label.qr_id?.slice(0, 8)].filter(Boolean).join(' · ') ||
    'QR label'
  );
}

export function buildLabelHtml({ labels, width, height, unit, title }) {
  const w = `${width}${unit}`;
  const h = `${height}${unit}`;
  const pages = labels
    .map((label) => {
      const img = label.qr_image
        ? `<img src="data:image/png;base64,${label.qr_image}" alt="QR" class="qr-img" />`
        : '<div class="qr-missing">No image</div>';
      const caption = escapeHtml(labelCaption(label));
      return `
        <div class="label-page">
          <div class="label-box">
            ${img}
            <p class="caption">${caption}</p>
          </div>
        </div>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: ${w} ${h}; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: system-ui, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .label-page {
      width: ${w};
      height: ${h};
      page-break-after: always;
      break-after: page;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5mm;
      overflow: hidden;
    }
    .label-page:last-child { page-break-after: auto; break-after: auto; }
    .label-box {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
    .qr-img {
      max-width: 92%;
      max-height: 78%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
    .caption {
      font-size: 6.5pt;
      line-height: 1.2;
      margin: 1.5mm 0 0;
      color: #222;
      word-break: break-all;
      max-width: 98%;
    }
    .qr-missing { color: #999; font-size: 10pt; }
    @media screen {
      body { background: #eee; padding: 16px; }
      .label-page {
        background: white;
        margin: 0 auto 16px;
        box-shadow: 0 1px 4px rgba(0,0,0,.15);
      }
    }
  </style>
</head>
<body>${pages}</body>
</html>`;
}

function waitForImages(doc) {
  const images = Array.from(doc.images || []);
  if (!images.length) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
          setTimeout(resolve, 3000);
        }),
    ),
  );
}

function printDocument(targetWindow) {
  return waitForImages(targetWindow.document).then(() => {
    targetWindow.focus();
    setTimeout(() => {
      try {
        targetWindow.print();
      } catch (_) {
        /* ignore */
      }
    }, 150);
  });
}

function printViaIframe(html) {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Print labels');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      iframe.remove();
      reject(new Error('Unable to create print frame. Try allowing pop-ups.'));
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    printDocument(iframe.contentWindow)
      .then(() => {
        setTimeout(() => {
          try {
            iframe.remove();
          } catch (_) {
            /* ignore */
          }
        }, 60_000);
        resolve();
      })
      .catch((err) => {
        iframe.remove();
        reject(err);
      });
  });
}

export function openLabelPrintSheet({
  labels,
  width,
  height,
  unit,
  title = 'QR Labels',
  printWindow = null,
}) {
  if (!labels?.length) return Promise.resolve();
  const html = buildLabelHtml({ labels, width, height, unit, title });

  let win = printWindow;
  if (win && win.closed) win = null;

  if (win) {
    try {
      win.document.open();
      win.document.write(html);
      win.document.close();
      return printDocument(win);
    } catch (err) {
      try {
        win.close();
      } catch (_) {
        /* ignore */
      }
      return printViaIframe(html);
    }
  }

  const popup = window.open('', '_blank', 'noopener,noreferrer');
  if (popup) {
    try {
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      return printDocument(popup);
    } catch (_) {
      try {
        popup.close();
      } catch (__) {
        /* ignore */
      }
      return printViaIframe(html);
    }
  }

  return printViaIframe(html);
}

/** Open a blank print tab immediately (user gesture) — fill with openLabelPrintSheet later. */
export function openPrintWindowLoading(message = 'Preparing labels…') {
  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) return null;
  try {
    win.document.open();
    win.document.write(
      `<!DOCTYPE html><html><head><title>Print</title></head><body style="font-family:system-ui;padding:24px;color:#334155">${escapeHtml(message)}</body></html>`,
    );
    win.document.close();
  } catch (_) {
    return null;
  }
  return win;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function safeFilePart(value, fallback = 'label') {
  return String(value || fallback)
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 48);
}

/** Download each label as a PNG file (staggered to avoid browser blocking). */
export async function downloadLabelsAsPng(labels, { basename = 'omnichek-qr' } = {}) {
  const withImages = (labels || []).filter((l) => l.qr_image);
  if (!withImages.length) throw new Error('No QR images to download');

  for (let i = 0; i < withImages.length; i += 1) {
    const label = withImages[i];
    const name = `${safeFilePart(basename)}-${String(i + 1).padStart(3, '0')}-${safeFilePart(label.qr_id || label.unit_code, 'qr')}.png`;
    const bin = atob(label.qr_image);
    const bytes = new Uint8Array(bin.length);
    for (let j = 0; j < bin.length; j += 1) bytes[j] = bin.charCodeAt(j);
    triggerDownload(new Blob([bytes], { type: 'image/png' }), name);
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 180));
  }
  return withImages.length;
}

/**
 * Build a multi-page PDF (one label per page) at the given physical size.
 */
export async function downloadLabelsAsPdf(
  labels,
  { width = 25, height = 25, unit = 'mm', filename = 'omnichek-qr-labels.pdf' } = {},
) {
  const withImages = (labels || []).filter((l) => l.qr_image);
  if (!withImages.length) throw new Error('No QR images to export');

  const pdfUnit = unit === 'in' ? 'in' : 'mm';
  const pdf = new jsPDF({
    orientation: width >= height ? 'landscape' : 'portrait',
    unit: pdfUnit,
    format: [Number(width), Number(height)],
  });

  const margin = pdfUnit === 'mm' ? 1.5 : 0.06;
  const usableW = Number(width) - margin * 2;
  const usableH = Number(height) - margin * 2;
  const qrBox = Math.min(usableW, usableH * 0.78);
  const captionH = usableH - qrBox;

  withImages.forEach((label, index) => {
    if (index > 0) pdf.addPage([Number(width), Number(height)], width >= height ? 'landscape' : 'portrait');
    const x = (Number(width) - qrBox) / 2;
    const y = margin;
    pdf.addImage(`data:image/png;base64,${label.qr_image}`, 'PNG', x, y, qrBox, qrBox);
    const caption = labelCaption(label);
    if (caption && captionH > 0) {
      pdf.setFontSize(pdfUnit === 'mm' ? 6.5 : 8);
      pdf.setTextColor(34, 34, 34);
      pdf.text(caption, Number(width) / 2, y + qrBox + captionH * 0.55, {
        align: 'center',
        maxWidth: usableW,
      });
    }
  });

  pdf.save(filename);
  return withImages.length;
}
